/** Minimal markdown → HTML for announcements/notes (safe subset, FLPBox pattern).
 * Also accepts WhatsApp-style markers common when pasting Hebrew from chat:
 *   *bold*  _italic_  ~strike~  (after standard **bold**).
 */

const BR_TOKEN = '\uE000BR\uE001';

export function escapeHtml(raw: string): string {
  return raw
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function isSafeHref(href: string): boolean {
  return /^https:\/\//i.test(href) || href.startsWith('/uploads/');
}

/** Allow explicit breaks without creating a blank-line paragraph (alternating strips). */
function protectLineBreaks(raw: string): string {
  return String(raw || '')
    // Literal <br> / <br/> / <br /> (only tag we re-admit after escape).
    .replace(/<br\s*\/?>/gi, BR_TOKEN)
    // Markdown hard break: backslash at end of line.
    .replace(/\\\r?\n/g, BR_TOKEN)
    // A line that is only "\" — extra blank visual line inside the same strip.
    .replace(/(^|\n)\\(?=\n|$)/g, `$1${BR_TOKEN}`);
}

/**
 * Inline emphasis: MD **bold**, then WhatsApp *bold* / _italic_ / ~strike~.
 * WhatsApp single-* bold is intentional (Hebrew paste); classic MD *italic* is not used.
 */
function applyInlineEmphasis(html: string): string {
  let out = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  // WhatsApp bold: *text* (remaining single pairs after ** handled).
  out = out.replace(/\*([^*\n]+)\*/g, '<strong>$1</strong>');
  // WhatsApp italic: _text_ — skip snake_case (my_site_tag).
  out = out.replace(/(?<![A-Za-z0-9])_([^_\n]+)_(?![A-Za-z0-9])/g, '<em>$1</em>');
  // WhatsApp strikethrough: ~text~
  out = out.replace(/~([^~\n]+)~/g, '<s>$1</s>');
  return out;
}

export function renderSafeMarkdown(
  raw: string,
  options?: { allowLinks?: boolean },
): string {
  const allowLinks = options?.allowLinks !== false;
  const escaped = escapeHtml(protectLineBreaks(raw));
  const withImages = escaped.replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, (_match, alt, src) => {
    const safeSrc = String(src || '');
    if (!isSafeHref(safeSrc)) return '';
    const altText = escapeHtml(String(alt || '').slice(0, 120));
    return `<img src="${safeSrc}" alt="${altText}" class="mt-2 max-h-32 w-auto rounded-[var(--radius-control)] border border-[var(--border-color)]" referrerpolicy="no-referrer" />`;
  });
  const withLinks = allowLinks
    ? withImages.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_match, text, href) => {
      const safeHref = String(href || '');
      if (!isSafeHref(safeHref)) return escapeHtml(String(text || ''));
      return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer nofollow" class="break-all underline text-[var(--accent-primary)]">${escapeHtml(String(text || ''))}</a>`;
    })
    : withImages.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_match, text) => escapeHtml(String(text || '')));
  const withEmphasis = applyInlineEmphasis(withLinks);
  const headingStyle = (level: 0 | 1 | 2 | 3) => {
    // em of note body (which uses --sf-font-size-base), not root rem
    const sizes = { 0: '1.75em', 1: '1.5em', 2: '1.125em', 3: '1em' };
    return `font-size:${sizes[level]};font-weight:700;margin:0.35em 0 0.15em;line-height:1.25;color:inherit`;
  };
  // #0 = display (H0); process before # so "#0 Title" is not eaten as H1.
  const withHeadings = withEmphasis
    .replace(/^#0\s+(.+)$/gm, `<p role="heading" aria-level="1" style="${headingStyle(0)}">$1</p>`)
    .replace(/^###\s+(.+)$/gm, `<h3 style="${headingStyle(3)}">$1</h3>`)
    .replace(/^##\s+(.+)$/gm, `<h2 style="${headingStyle(2)}">$1</h2>`)
    .replace(/^#\s+(.+)$/gm, `<h1 style="${headingStyle(1)}">$1</h1>`);
  const withLists = withHeadings.replace(
    /(?:^|\n)((?:-\s+.+(?:\n|$))+)/g,
    (_match, block: string) => {
      const items = block
        .trim()
        .split('\n')
        .map((line) => line.replace(/^-\s+/, '').trim())
        .filter(Boolean)
        .map((item) => `<li>${item}</li>`)
        .join('');
      return `\n<ul class="my-1 list-disc ps-5">${items}</ul>\n`;
    },
  );
  const withOrderedLists = withLists.replace(
    /(?:^|\n)((?:\d+\.\s+.+(?:\n|$))+)/g,
    (_match, block: string) => {
      const items = block
        .trim()
        .split('\n')
        .map((line) => line.replace(/^\d+\.\s+/, '').trim())
        .filter(Boolean)
        .map((item) => `<li>${item}</li>`)
        .join('');
      return `\n<ol class="my-1 list-decimal ps-5">${items}</ol>\n`;
    },
  );
  return withOrderedLists
    .replace(new RegExp(BR_TOKEN, 'g'), '<br />')
    .replace(/\n/g, '<br />');
}
